import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AccountGroup } from '@galaxy/account-group';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import { ProductActivationPrereqFormComponent } from '@vendasta/businesses';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { FulfillmentFormConfigInterface, FulfillmentOrder, FulfillmentOrderStatus } from '@vendasta/order-fulfillment';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  CommonField,
  ConfigInterface,
  CustomField,
  Field,
  Order,
  SalesOrdersService,
  UserInterface as User,
} from '@vendasta/sales-orders';
import { OrderFormOptionsInterface } from '@vendasta/store';
import { WorkOrderFormConfigService, WorkOrderSalesOrderSummaryService } from '@vendasta/work-order';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { DEMO_SSC_UPLOAD_URL, PROD_SSC_UPLOAD_URL } from '../../shared/constants';
import { LineItemsComponent } from '../line-items/line-items.component';
import { OrderFormComponent } from '../order-form/order-form.component';

@Component({
  selector: 'orders-edit-order',
  templateUrl: './edit-order.component.html',
  styleUrls: [
    '../../shared/shared-styles.scss',
    '../create-order/create/create-order.component.scss',
    './edit-order.component.scss',
  ],
})
export class EditOrderComponent implements OnInit, OnDestroy {
  @Input() business: Pick<AccountGroup, 'externalIdentifiers' | 'napData' | 'accountGroupId'>;
  @Input() users: User[];
  @Input() fileUploadUrl: string;
  @Input() productIds: string[];
  @Input() fulfillmentFormLink: string;
  @Input() hideNotesSections?: boolean;
  @Input() hideTagsSection?: boolean;
  @Input() hideAttachmentsSection?: boolean;
  @Input() hideOrderFormSaveButton?: boolean;

  // let the parent component (in the client) be responsible for refreshing the order
  // so that other child components on the page respond accordingly
  @Output() refreshOrderDetails = new EventEmitter<boolean>();
  @Input() set order(order: Order) {
    this.order$$.next(order);
  }

  @ViewChild('lineItems') lineItemsComponent: LineItemsComponent;
  @ViewChild('productPrereqForm') readonly productPrereqForm: ProductActivationPrereqFormComponent;
  @ViewChild('orderForm') readonly salesOrderForm: OrderFormComponent;

  private readonly order$$ = new BehaviorSubject<Order>(null);
  order$: Observable<Order> = this.order$$.asObservable();

  orderFormOptions$: Observable<OrderFormOptionsInterface>;

  orderDetailsFormGroup: UntypedFormGroup;
  orderFormGroup: UntypedFormGroup;
  subscriptions: Subscription[] = [];
  attachmentsUploadUrl = PROD_SSC_UPLOAD_URL;
  orderConfig$: Observable<ConfigInterface>;
  hasAtLeastOneForm$: Observable<boolean>;
  shouldShowDetailsNeededBanner$: Observable<boolean>;

  constructor(
    private environmentService: EnvironmentService,
    private salesOrdersService: SalesOrdersService,
    private workOrderFormConfigService: WorkOrderFormConfigService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly workOrderSalesOrderSummaryService: WorkOrderSalesOrderSummaryService,
  ) {
    this.orderDetailsFormGroup = this.formBuilder.group({});
    this.orderFormGroup = this.formBuilder.group({});
  }

  ngOnInit(): void {
    if (this.environmentService.getEnvironment() === Environment.DEMO) {
      this.attachmentsUploadUrl = DEMO_SSC_UPLOAD_URL;
    }

    this.orderConfig$ = this.salesOrdersService
      .getConfig(this.business.externalIdentifiers.partnerId, this.business.accountGroupId)
      .pipe(shareReplay(1));

    this.orderFormOptions$ = this.orderConfig$.pipe(
      map((config: ConfigInterface) => {
        return {
          readOnly: false,
          showOfficeUseQuestions: true,
          bypassRequiredQuestions: !config.salespersonOptions.validateRequiredFields,
        };
      }),
    );

    const formConfigs$: Observable<Map<string, FulfillmentFormConfigInterface>> = this.order$.pipe(
      switchMap((order) => {
        return this.salesOrdersService.previewOrderActivations(order.orderId, order.businessId).pipe(
          map((activations) => {
            const productIds: string[] = activations?.map((activation) => activation.productId) || [];
            return Array.from(new Set(productIds));
          }),
          switchMap((productIds) => {
            const formConfigs = productIds.map((productId) =>
              this.workOrderFormConfigService.getFormConfig(order.orderId, productId, order.businessId),
            );
            return combineLatest(formConfigs).pipe(
              map((configs) => {
                const productIdToConfigMap: Map<string, FulfillmentFormConfigInterface> = new Map();
                productIds.forEach((productId, i) => {
                  productIdToConfigMap.set(productId, configs[i]);
                });
                return productIdToConfigMap;
              }),
            );
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.hasAtLeastOneForm$ = formConfigs$.pipe(
      map((formConfigs) => {
        return Array.from(formConfigs.values()).some((config) => {
          return config?.orderForm?.length > 0;
        });
      }),
    );

    const workOrders$: Observable<FulfillmentOrder[]> = this.order$.pipe(
      switchMap((order) =>
        this.workOrderSalesOrderSummaryService.getWorkOrdersForSalesOrder(order.orderId, order.businessId),
      ),
      map((workOrders) => workOrders.filter((workOrder) => Object.hasOwnProperty.call(workOrder, 'status'))),
    );

    this.shouldShowDetailsNeededBanner$ = workOrders$.pipe(
      map((workOrders) =>
        workOrders.some((wo) => wo.status === FulfillmentOrderStatus.FULFILLMENT_ORDER_STATUS_DETAILS_NEEDED),
      ),
    );

    this.subscriptions.push(
      this.hasAtLeastOneForm$.pipe(take(1)).subscribe((hasFormConfig) => {
        if (hasFormConfig) {
          this.analyticsService.trackEvent('draft-order-viewed', 'page-view', 'page-view');
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  hasUnsavedChanges(): Observable<boolean> {
    return this.productPrereqForm.hasUnsavedChanges$;
  }

  validateForm(): Observable<boolean> {
    return this.productPrereqForm.isValid$.pipe(
      map((isValid) => {
        if (!isValid) {
          // todo: add translations to translation files
          this.snackbarService.openErrorSnack('SALES_ORDERS.ERRORS.PREREQ_FORM_IS_NOT_VALID');
          return false;
        }
        if (!this.salesOrderForm.validateForm()) {
          this.snackbarService.openErrorSnack('SALES_ORDERS.ERRORS.FORM_IS_NOT_VALID');
          return false;
        }
        return true;
      }),
    );
  }

  saveOrderFormIfDirty(): Observable<boolean> {
    if (this.orderFormGroup.dirty) {
      return this.salesOrderForm.updateAnswers();
    }
    return of(true);
  }

  // updates the line items on the order and triggers the parent component to update the order reference
  saveUpdatedLineItems(): void {
    this.order$
      .pipe(
        switchMap((order) => {
          return this.salesOrdersService.updateLineItems(
            order.orderId,
            order.businessId,
            this.lineItemsComponent.getCurrentLineItems(),
          );
        }),
        take(1),
      )
      .subscribe(() => {
        this.refreshOrderDetails.emit(true);
      });
  }

  /**
   * returns the data from common form fields in a format that is usable by sales orders
   */
  public getCommonFormData(): CommonField[] {
    return this.salesOrderForm.getCommonFormData();
  }

  /**
   * returns the data from custom forms in a format that is usable by sales orders
   */
  public getCustomFormData(): CustomField[] {
    return this.salesOrderForm.getCustomFormData();
  }

  /**
   * returns the data from extra fields in a format that is usable by sales orders
   */
  public getExtraFieldsData(): Field[] {
    return this.salesOrderForm.getExtraFieldsData();
  }

  public isValid(): Observable<boolean> {
    return this.productPrereqForm.isValid$;
  }

  public printPage(): void {
    this.salesOrderForm.openDropdown();
  }
}
